import { InfoCircleIcon, XIcon } from '@langchain/untitled-ui-icons';
import { FormControl } from '@mui/joy';

import { produce } from 'immer';
import { useEffect, useRef, useState } from 'react';

import {
  defaultEvaluatorOutputSchema,
  emptyStructuredPromptManifest,
} from '@/Pages/HubPlayground/HubPlayground.utils';
import { PromptSettingsDisplay } from '@/Pages/Playground/components/PromptSettings';
import { Manifest } from '@/Pages/Playground/components/manifest/Manifest';
import {
  createPlaygroundManifest,
  defaultChatModelManifest,
  getNonRunnableBindingModelFromManifest,
} from '@/Pages/Playground/utils/Playground.utils';
import { PromptSelector } from '@/Pages/Prompts/components/PromptSelector';
import { Skeleton } from '@/Pages/Run/components/InputOutputLoadingSkeleton';
import { V2_EVALUATOR_VERSION } from '@/Pages/Settings/utils/constants';
import {
  convertSerializedMessagesToMessageTuple,
  parseOwnerRepoCommit,
} from '@/components/Rules/RulesEvaluatorPane.utils';
import { Button, IconButton, Text, Tooltip } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useCommit, useRepo } from '@/hooks/useSwr';
import { CommitManifestResponse, MessageCapability } from '@/types/schema';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import {
  BackfillDateInput,
  BackfillFromFormLabel,
} from '../Rules/SessionRulesCrudFormV2';
import { SearchModel } from '../RunsTable/types';
import { Switch } from '../Switch/Switch';
import useToast from '../Toast/useToast';
import { EvaluatorFeedbackSchema } from './EvaluatorFeedbackSchema';
import { EvaluatorFewShotConfig } from './EvaluatorFewShotConfig';
import { EvaluatorHubPromptPlaygroundLink } from './HubPromptPlaygroundLink';
import { PromptPreview } from './PromptPreview';
import { useAvailablePaths } from './hooks/useAvailablePaths';
import { useEvaluatorStore } from './store/evaluatorStore';
import { EvaluatorCrudFormProps } from './types';
import { shouldShowOlderVersionMessage as shouldShowOlderVersionMessageUtil } from './utils/shouldShowOlderVersionMessage';
import {
  useDatasetInputOutputData,
  useSessionInputOutputData,
} from './utils/useInputOutputData';
import {
  hasMultimodalContent,
  useParsedManifest,
} from './utils/useParsedManifest';
import { useVariableMappingProps } from './utils/useVariableMappingProps';
import {
  formatValidationErrorMessage,
  validateSchemaPropertyKeys,
} from './utils/validateOutputSchemaKeys';

export function EvaluatorCrudForm(props: EvaluatorCrudFormProps) {
  const [preview, setPreview] = useState<boolean>(false);
  const reset = useEvaluatorStore((state) => state.reset);
  const { createToast } = useToast();

  const setModel = useEvaluatorStore((state) => state.setModel);
  const setPromptMessages = useEvaluatorStore(
    (state) => state.setPromptMessages
  );
  const setFeedbackSchema = useEvaluatorStore(
    (state) => state.setFeedbackSchema
  );
  const updateMappingWithPromptManifest = useEvaluatorStore(
    (state) => state.updateMappingWithPromptManifest
  );

  const fewShotEnabled = useEvaluatorStore((state) => state.fewShotEnabled);
  const promptTemplateFormat = useEvaluatorStore(
    (state) => state.promptTemplateFormat
  );
  const evaluatorType = useEvaluatorStore((state) => state.evaluatorType);

  // For hub prompt selector
  const hubRef = useEvaluatorStore((state) => state.hubRef);
  const setHubRef = useEvaluatorStore((state) => state.setHubRef);
  const setEvaluatorType = useEvaluatorStore((state) => state.setEvaluatorType);
  const ffEvaluatorVersion = Number(
    useOrgConfig(OrgConfigs.new_rule_evaluator_creation_version).value
  );
  const storeEvaluatorVersion = useEvaluatorStore(
    (state) => state.evaluatorVersion
  );
  const evaluatorVersion = props.isCreating
    ? ffEvaluatorVersion
    : storeEvaluatorVersion;

  const setFeedbackEditorType = useEvaluatorStore(
    (state) => state.setFeedbackEditorType
  );
  const setHubRefManifest = useEvaluatorStore(
    (state) => state.setHubRefManifest
  );

  // Determine if prompt and schema should be read-only
  const isManifestReadyOnly = evaluatorType === 'hub' && evaluatorVersion < 3;

  const isSchemaReadOnly = isManifestReadyOnly || Boolean(props.alignQueueId);

  const [owner, repo, commitHash] = parseOwnerRepoCommit(hubRef);
  const { data: prompt, isLoading: isLoadingPrompt } = useRepo(owner, repo);

  const [pendingHubRef, setPendingHubRef] = useState<string>('');
  // For checking multimodal content when selecting a prompt
  const [pendingOwner, pendingRepo, pendingCommitHash] = pendingHubRef
    ? parseOwnerRepoCommit(pendingHubRef)
    : [];

  useCommit({
    owner: pendingOwner,
    repo: pendingRepo ?? '',
    sha: pendingCommitHash ?? 'latest',
    get_examples: false,
    include_model: evaluatorVersion >= 3,
    options: {
      revalidateOnFocus: false,
      dedupingInterval: 0,
      keepPreviousData: false,
      onSuccess: (pendingCommitData: CommitManifestResponse) => {
        // Check for multimodal content when pendingCommitData loads
        if (pendingHubRef && pendingCommitData) {
          const modelToUse =
            currentModel ??
            getNonRunnableBindingModelFromManifest(
              pendingCommitData.manifest
            ) ??
            defaultChatModelManifest;

          const testManifest = createPlaygroundManifest(
            (evaluatorVersion >= 3
              ? pendingCommitData?.manifest?.kwargs?.first
              : pendingCommitData?.manifest) ??
              emptyStructuredPromptManifest('dataset-eval', null, 'mustache'),
            modelToUse
          );

          if (hasMultimodalContent(testManifest)) {
            createToast({
              title: 'Multimodal prompts not supported',
              description:
                'Multimodal prompts are not supported for evaluators yet. Support is coming soon!',
              type: 'warning',
            });

            setPendingHubRef('');
            return;
          }

          // Check for invalid output schema property keys
          const kwargs =
            evaluatorVersion >= 3
              ? testManifest.kwargs.first?.kwargs
              : testManifest.kwargs;
          const outputSchema = kwargs?.schema || kwargs?.schema_;

          if (outputSchema) {
            const { isValid, invalidKeys } =
              validateSchemaPropertyKeys(outputSchema);
            if (!isValid) {
              createToast({
                title: 'Selected prompt has invalid output schema',
                description: formatValidationErrorMessage(invalidKeys),
                type: 'warning',
              });

              setPendingHubRef('');
              return;
            }
          }

          // No multimodal content and valid schema keys, proceed with setting the hubRef
          setHubRef(pendingHubRef);
          setEvaluatorType('hub');
          if (evaluatorVersion < 3) {
            setFeedbackEditorType('schema');
          }
          setPendingHubRef('');
          setHubRefManifest(pendingCommitData.manifest);
        }
      },
    },
  });

  // Check if we should show the "older version" message
  const shouldShowOlderVersionMessage =
    shouldShowOlderVersionMessageUtil({
      evaluatorType,
      evaluatorVersion,
      commitHash,
      latestCommitHash: prompt?.repo.last_commit_hash ?? undefined,
    }) && !isLoadingPrompt;

  const { manifest, disabledMessageTypes, isHubManifestLoading } =
    useParsedManifest(props.evaluatedEntityType);

  // For rich text editor variable mapping dropdown
  const variableMappingProps = useVariableMappingProps();
  const currentModel = useEvaluatorStore((state) => state.model);

  useEffect(() => {
    return () => {
      // Necessary in the evaluator details page. When the evaluator is edited, we refetch it and display the new one.
      // However, if this refetch completes before the component unmounts, then we will call reset() and the page will be blank.
      if (!props.preventReset) {
        reset();
      }
    };
  }, [reset, props.preventReset]);

  const renderManifest = () => {
    if (evaluatorType === 'hub' && isHubManifestLoading) {
      return (
        <div className="pt-4">
          <Skeleton lines={3} />
        </div>
      );
    }

    return (
      <Tooltip
        title={
          evaluatorType === 'hub' &&
          evaluatorVersion < 3 && (
            <div className="max-w-[350px]">
              <div className="text-sm font-semibold">
                Saved prompts cannot be edited in this view
              </div>
              <div className="text-sm font-normal text-gray-300 dark:text-gray-600">
                To make changes, go to the <EvaluatorHubPromptPlaygroundLink />.
                Your latest changes will appear here automatically.
              </div>
            </div>
          )
        }
        side="top"
      >
        <div>
          <Manifest
            // Need to rerender manifeset when fewShotEnabled changes, it inserts or removes {{few_shot_examples}} variable
            key={`${evaluatorType}-${hubRef}-${fewShotEnabled}-${promptTemplateFormat}`}
            variant="evaluator"
            value={manifest.kwargs.first}
            onChange={(promptManifest) => {
              if (evaluatorType === 'hub' && evaluatorVersion < 3) {
                return;
              }
              try {
                const messagesTuple = convertSerializedMessagesToMessageTuple(
                  promptManifest.kwargs.messages
                );

                setPromptMessages(messagesTuple);
                setHubRefManifest(
                  produce(manifest, (draft) => {
                    draft.kwargs.first.kwargs.messages =
                      promptManifest.kwargs.messages;
                    draft.kwargs.first.kwargs.input_variables =
                      promptManifest.kwargs.input_variables;
                  })
                );
                updateMappingWithPromptManifest(promptManifest);
              } catch {
                // quite often as users type, the prompt manifest is invalid
                // so we catch the error and do nothing
              }
            }}
            hideTemplateFormatSelector
            outputSchemaAsModal
            minimizedSchema={true}
            disabledMessageTypes={disabledMessageTypes}
            disabledMessageCapabilities={[
              MessageCapability.IMAGE,
              MessageCapability.CANVAS,
            ]}
            isAllCollapsed={false}
            readOnly={isManifestReadyOnly}
            options={{}}
            onOptionsChange={() => void 0}
            variableMappingProps={variableMappingProps}
          />
        </div>
      </Tooltip>
    );
  };

  const [dismissedVariableMappingInfo, setDismissedVariableMappingInfo] =
    useLocalStorageState(
      'ls:evaluator-crud-form:dismissed-variable-mapping-info',
      false
    );

  const [storedConfigIdToLoad, setStoredConfigIdToLoad] = useState<
    string | undefined
  >(undefined);

  return (
    <div>
      <div className="pb-2">{props.renderEvaluatorName?.()}</div>
      {props.backfillComponent && props.backfillComponent}
      <div className="flex flex-col">
        <div className="ml-5">
          <label className="block pb-1.5 font-semibold">Prompt & Model</label>

          <p className="pb-3 text-sm text-secondary">
            Create your evaluator by defining the criteria you want to evaluate
            in your prompt. Create your prompt below or use a saved prompt from
            Prompt Hub.
          </p>

          {props.showEvaluatorVersionWarning && (
            <div className="mb-2 flex items-center gap-1 rounded-md bg-warning-primary p-2">
              <span className="text-sm text-warning">
                Please update variable mappings to ensure compatibility with the
                latest system format.
              </span>
            </div>
          )}

          {evaluatorVersion >= 3 &&
            (shouldShowOlderVersionMessage ? (
              <div className="mb-2 rounded-md border border-warning bg-warning-primary p-1 text-xs">
                You're viewing an older version of this prompt. Committing
                changes will overwrite the latest version.
              </div>
            ) : (
              <div className="mb-2 flex items-center gap-2 rounded-md bg-brand-tertiary px-3 py-4 text-xs">
                <InfoCircleIcon className="h-3 w-3 text-brand-primary" />
                <Text
                  className="text-brand-primary"
                  variant="md"
                  weight="medium"
                >
                  Saving the evaluator will create a new{' '}
                  {hubRef === '' ? 'prompt ' : 'commit for this prompt'} in the
                  hub.
                </Text>
              </div>
            ))}

          <div className="flex justify-between gap-2 pb-3">
            <div className="flex items-center gap-1">
              <PromptSelector
                selectedPromptIdentifier={hubRef ? hubRef : 'Use hub prompt'}
                prompt={prompt?.repo}
                tags={['StructuredPrompt']}
                showNewPromptLink={false}
                onChange={(prompt, commit) => {
                  const newHubRef = `${prompt.repo_handle}:${
                    commit?.commit_hash.slice(0, 8) || 'latest'
                  }`;

                  // Set pending hubRef to trigger multimodal check
                  setPendingHubRef(newHubRef);
                  // Set current model
                  const newModel = prompt?.latest_commit_manifest?.manifest
                    ? getNonRunnableBindingModelFromManifest(
                        prompt?.latest_commit_manifest?.manifest
                      )
                    : null;
                  if (newModel) {
                    setModel(newModel);
                  }
                }}
                showCommitSelector={true}
                selectByLatest={true}
                emptyStateText="No structured prompts found."
                className="min-w-[50px] max-w-[200px] flex-shrink text-sm"
                fullInfo={true}
                hidePublicPrompts={true}
              />
              {evaluatorType === 'hub' && (
                <IconButton
                  icon={XIcon}
                  onClick={() => {
                    setHubRef('');
                    setEvaluatorType('prompt');
                    setFeedbackEditorType('pretty');

                    const manifestType = props.evaluatedEntityType;

                    // Reset feedback schema to default values
                    const defaultSchema =
                      defaultEvaluatorOutputSchema(manifestType);
                    setFeedbackSchema(defaultSchema);
                  }}
                  label="Deselect hub prompt"
                  size="sm"
                  color="secondary"
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* temporary gate to not show preview until we're ready to support it in threads */}
              {props.setShowSampleData && (
                <Switch
                  checked={preview}
                  onChange={setPreview}
                  label="Preview"
                  size="md"
                  labelClassName="text-sm"
                  labelPosition="left"
                />
              )}
              {props.setShowSampleData && (
                <Button
                  variant="outlined"
                  color={props.showSampleData ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() =>
                    props.setShowSampleData?.(!props.showSampleData)
                  }
                >
                  See Sample
                </Button>
              )}
              <PromptSettingsDisplay
                variant="evaluator"
                setManifest={(manifest) => {
                  setModel(manifest.kwargs.last);
                }}
                manifest={manifest}
                storedConfigIdToLoad={storedConfigIdToLoad}
                setStoredConfigIdToLoad={setStoredConfigIdToLoad}
              />
            </div>
          </div>
        </div>
        <div
          className={cn(
            'mb-2 ml-5 grid h-0 grid-cols-[auto_1fr_auto] items-center gap-2 overflow-hidden rounded-md border border-secondary bg-secondary text-xs transition-all duration-300',
            variableMappingProps &&
              !dismissedVariableMappingInfo &&
              'h-auto px-3 py-2.5'
          )}
        >
          <Text
            className="text-secondary opacity-100"
            variant="md"
            weight="medium"
          >
            Start typing{' '}
            <span className="font-bold text-primary">
              {promptTemplateFormat === 'f-string' ? '{' : '{{'}
            </span>{' '}
            to create a variable.
          </Text>
          <div className="flex justify-end">
            <IconButton
              label="Dismiss"
              size="sm"
              color="secondary"
              className="bg-secondary"
              variant="plain"
              icon={XIcon}
              onClick={() => {
                setDismissedVariableMappingInfo(true);
              }}
            />
          </div>
        </div>
        <div
          className={cn(
            (preview || isManifestReadyOnly) && 'ml-5',
            'max-h-[50vh] overflow-y-auto'
          )}
        >
          {preview ? (
            <PromptPreview
              {...props}
              manifest={manifest}
              evaluatorType={evaluatorType}
            />
          ) : (
            renderManifest()
          )}
        </div>
      </div>
      <div className="mb-5 ml-5 mt-10">
        <EvaluatorFeedbackSchema
          evaluatedEntityType={props.evaluatedEntityType}
          isAlignEvaluator={!!props.alignQueueId}
          readOnly={isSchemaReadOnly}
        />
      </div>
      {!props.hideFewShotConfig && (
        <div className="ml-5">
          <EvaluatorFewShotConfig
            fewShotDatasetId={props.fewShotDatasetId}
            evaluatedEntityType={props.evaluatedEntityType}
          />
        </div>
      )}
    </div>
  );
}

export function DatasetEvaluatorCrudForm(props: {
  datasetId: string;
  fewShotDatasetId?: string;
  alignQueueId?: string;
  evaluatorVersion?: number;
  isCreating?: boolean;
  preventReset?: boolean;
}) {
  const { input, output, referenceOutput, isLoading } =
    useDatasetInputOutputData(props.datasetId);
  const name = useEvaluatorStore((state) => state.name);
  const setName = useEvaluatorStore((state) => state.setName);

  const isEdited = useEvaluatorStore((state) => state.edited);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Focus the name input after component mounts
    if (inputRef.current && props.isCreating) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, []);

  const showNameError = !name && isEdited;
  const renderEvaluatorName = () => {
    return (
      <div className="ml-5 flex flex-col gap-2">
        <label className="mb-2 font-semibold">Evaluator Name</label>
        <input
          ref={inputRef}
          autoFocus={props.isCreating}
          placeholder="eg. Correctness"
          className={cn(
            'rounded-md border border-secondary bg-background px-3 py-2 text-sm ring-0 ',
            showNameError
              ? 'border-error focus:border-error'
              : 'border-secondary hover:border-primary focus:border-brand'
          )}
          name="value"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {showNameError && (
          <span className="text-sm text-error">Rule name is required</span>
        )}
      </div>
    );
  };
  const [applyToPastRuns, setApplyToPastRuns] = useState(false);
  const setBackfillFrom = useEvaluatorStore((state) => state.setBackfillFrom);
  const backfillFrom = useEvaluatorStore((state) => state.backfillFrom);
  const errors = useEvaluatorStore((state) => state.errors);

  const backfillComponent = (
    <>
      {props.isCreating && (
        <div className="ml-5 pb-6">
          <div className="flex flex-row items-center gap-2 pb-2">
            <div className="text-sm font-semibold">Apply to past runs</div>
            <Switch
              size="md"
              label=""
              checked={applyToPastRuns}
              onChange={setApplyToPastRuns}
            />
          </div>
          {applyToPastRuns && (
            <FormControl
              error={!!errors.backfillFrom}
              sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}
            >
              <BackfillFromFormLabel disabled={false} />
              <BackfillDateInput
                value={backfillFrom ?? undefined}
                onChange={(value) => {
                  setBackfillFrom(value);
                }}
                disabled={false}
                error={
                  errors.backfillFrom
                    ? { type: 'validate', message: errors.backfillFrom }
                    : undefined
                }
              />
            </FormControl>
          )}
        </div>
      )}
      {backfillFrom && !props.isCreating && (
        <div className="ml-5 pb-6">
          <FormControl
            sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}
          >
            <BackfillFromFormLabel disabled={true} />
            <BackfillDateInput
              value={backfillFrom}
              onChange={(value) => {
                setBackfillFrom(value);
              }}
              disabled={true}
            />
          </FormControl>
        </div>
      )}
    </>
  );

  const evaluatorVersion = props.evaluatorVersion ?? V2_EVALUATOR_VERSION;

  return (
    <EvaluatorCrudForm
      input={input ?? {}}
      output={output ?? {}}
      referenceOutput={referenceOutput ?? {}}
      isLoading={isLoading}
      fewShotDatasetId={props.fewShotDatasetId}
      alignQueueId={props.alignQueueId}
      showEvaluatorVersionWarning={evaluatorVersion < V2_EVALUATOR_VERSION}
      renderEvaluatorName={renderEvaluatorName}
      isCreating={props.isCreating}
      backfillComponent={backfillComponent}
      preventReset={props.preventReset}
      evaluatedEntityType="dataset-eval"
    />
  );
}

export function SessionEvaluatorCrudForm(props: {
  sessionId: string;
  filters: SearchModel;
  fewShotDatasetId?: string;
  evaluatorVersion?: number;
  isCreating?: boolean;
  showSampleData?: boolean;
  setShowSampleData?: (show: boolean) => void;
  alignQueueId?: string;
}) {
  const { input, output, isLoading } = useSessionInputOutputData({
    sessionId: props.sessionId,
    filters: props.filters,
  });
  useAvailablePaths(input, output, undefined, isLoading);

  return (
    <EvaluatorCrudForm
      input={input ?? {}}
      output={output ?? {}}
      isLoading={isLoading}
      fewShotDatasetId={props.fewShotDatasetId}
      showEvaluatorVersionWarning={
        Boolean(props.evaluatorVersion) &&
        (props.evaluatorVersion ?? 0) < V2_EVALUATOR_VERSION
      }
      isCreating={props.isCreating}
      showSampleData={props.showSampleData}
      setShowSampleData={props.setShowSampleData}
      alignQueueId={props.alignQueueId}
      evaluatedEntityType="session-eval"
    />
  );
}
